import clsx from "clsx";
import { AnimatePresence, motion, useInView } from "framer-motion";
import React, { FC, useCallback, useEffect, useRef } from "react";

import { useDataLayer } from "../../hooks/use-data-layer";
import { GoogleAnalyticsEvent } from "../../utils/analytics";
import { stringToHash } from "../../utils/hash";
import { getSearchParams, getUpdatedSearchParamsUrl } from "../../utils/search-params";
import { ExhibitionCard, ExhibitionCardProps } from "../cards/exhibition-card";
import { PaginationNumbers } from "../pagination/pagination-numbers";

export interface ExhibitionsGridProps {
  exhibitions: Queries.ExhibitionCardDataFragment[];
  labels: ExhibitionCardProps["labels"] & {
    "exhibitions-grid-empty-state": string;
  };
  pageSize?: number;
  pageSearchParam?: string;
}

export const ExhibitionsGrid: FC<ExhibitionsGridProps> = ({
  exhibitions,
  labels,
  pageSize = 25,
  pageSearchParam = "exhibitions-page",
}) => {
  const hashKey = stringToHash(exhibitions.map((e) => e.id).join(""));
  const searchParams = getSearchParams();
  const page = parseInt(searchParams?.get(pageSearchParam) ?? "0");
  const currentPage = exhibitions.slice(page * pageSize, (page + 1) * pageSize);

  const totalPages = Math.ceil((exhibitions.length ?? 0) / pageSize);

  // Send an event the first time the exhibition grid is viewed (and only first time)
  const gridRef = useRef<HTMLDivElement>(null);
  const isExhibitionsGridVisible = useInView(gridRef);
  const hasSentViewEvent = useRef(false);
  const { pushEvent } = useDataLayer();
  useEffect(() => {
    if (currentPage.length && isExhibitionsGridVisible && !hasSentViewEvent.current) {
      pushEvent({
        event: GoogleAnalyticsEvent.ViewExhibitionsList,
      });
      hasSentViewEvent.current = true;
    }
  }, [isExhibitionsGridVisible, pushEvent, currentPage]);

  const onSetCurrentPageIndex = useCallback((i: number) => {
    if (typeof window !== "undefined") {
      window.location.href = getUpdatedSearchParamsUrl({ page: i });
    }
  }, []);

  return (
    <div ref={gridRef}>
      <AnimatePresence initial={false} mode="popLayout">
        {currentPage.length ? (
          <motion.div
            className="h-full flex w-full flex-col justify-stretch"
            key={hashKey}
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
            transition={{
              opacity: { ease: "easeOut", duration: 0.5 },
            }}
          >
            <>
              <div
                className={clsx(
                  "grid gap-4 lg:gap-6 items-stretch justify-center",
                  "grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4"
                )}
              >
                {currentPage.filter(Boolean).map((exhibition, i) => (
                  <ExhibitionCard fullWidth labels={labels} {...exhibition} key={i} />
                ))}
              </div>
              {currentPage.length !== exhibitions.length && (
                <div className="w-full flex justify-center pt-4">
                  <PaginationNumbers
                    currentPageIndex={page}
                    getItemHref={(i) => getUpdatedSearchParamsUrl({ page: i })}
                    setCurrentPageIndex={onSetCurrentPageIndex}
                    totalPages={totalPages}
                  />
                </div>
              )}
            </>
          </motion.div>
        ) : (
          <div className="container mx-auto h-64 flex items-center justify-center">
            <p>{labels["exhibitions-grid-empty-state"]}</p>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
